import { getMarketByTokenIndex, MARKET_CATEGORIES } from "../../config/markets";
import { NumberTicker } from "./NumberAnimation";
import useMarketOpenCountdown from "./hooks/useMarketOpenCountdown";
import usePokeIndexMarketOpenCountdown from "./hooks/usePokeIndexMarketOpenCountdown";
import useWatchMarketCountdown from "./hooks/useWatchMarketCountdown";
import useScheduledMarketCountdown from "./hooks/useScheduledMarketCountdown";
import usePsa10MarketCountdown from "./hooks/usePsa10MarketCountdown";

/**
 * "Next Price Update" / "Market Opens In" countdown pill for every market that
 * is not always trading: the oracle-priced ones (real estate, the Pokémon
 * index, watches) and the scheduled ones (stocks, commodities).
 *
 * This used to be three near-identical blocks inside `TradingTopBar`, each in
 * its own `justify-end` row above the bar — a full row of vertical space for a
 * 24px pill. It now lives as an overlay pinned to the top-right of the chart,
 * so it costs no layout height at all. The three market kinds are one component
 * because the only thing that differs between them is which countdown hook
 * feeds it.
 *
 * All three hooks run on every render — hooks cannot be called conditionally —
 * which is cheap: each is a 1s interval over a fixed schedule.
 *
 * @param {{tokenIndex: number|string, className?: string, style?: object, stacked?: boolean}} props
 *   `className`/`style` override the default absolute placement, for callers
 *   that want the pill inline instead. `stacked` sets the label over the clock
 *   rather than beside it, for the header row where every neighbouring stat is
 *   a caption with its figure underneath — a single-line pill in that row read
 *   as a button sitting among the numbers rather than as one of them.
 */
const PriceUpdateBadge = ({ tokenIndex, className = "", style, stacked = false }) => {
  const realEstate = useMarketOpenCountdown();
  const pokeIndex = usePokeIndexMarketOpenCountdown();
  const watch = useWatchMarketCountdown();
  const psa10 = usePsa10MarketCountdown();

  const market = getMarketByTokenIndex(tokenIndex);
  // Stocks and commodities close overnight and at weekends. Their pill used to
  // sit in its own `justify-end` row above the whole top bar; it belongs with
  // the other countdowns, over the plot it is about.
  const scheduled = useScheduledMarketCountdown(market?.tradingSchedule || null);

  let countdown = null;
  let scheduledKind = false;
  if (market?.category === MARKET_CATEGORIES.REAL_ESTATE) countdown = realEstate;
  else if (market?.id === "poke" || market?.chartType === "poke") countdown = pokeIndex;
  else if (market?.category === MARKET_CATEGORIES.WATCHES) countdown = watch;
  else if (market?.chartType === "psa10") countdown = psa10;
  else if (market?.tradingSchedule) { countdown = scheduled; scheduledKind = true; }

  if (!countdown) return null;

  const { isMarketOpen, timeRemaining } = countdown;
  const formatTime = (value) => String(value).padStart(2, "0");
  const clock = `${formatTime(timeRemaining.hours)}:${formatTime(timeRemaining.minutes)}:${formatTime(timeRemaining.seconds)}`;

  return (
    <div
      className={`pointer-events-none ${stacked ? "flex flex-col items-center justify-center" : "flex items-center"} ${className}`}
      style={{
        position: "absolute",
        top: 8,
        right: 12,
        zIndex: 5,
        ...(stacked
          ? { borderRadius: "999px", padding: "3px 12px", gap: "2px" }
          : { height: "24px", borderRadius: "999px", padding: "4px 10px", gap: "8px" }),
        // A pill in the accent wash, no border — nothing on this page carries
        // both a fill and a hairline. Open markets keep the semantic green.
        border: 0,
        backgroundColor: isMarketOpen
          ? "rgba(34, 149, 100, 0.10)"
          : "var(--cat-accent-soft, rgba(91, 29, 238, .10))",
        // Pinned over the plot it needs its own backdrop rather than relying on
        // the card behind it — the recessed chart well is not a flat fill.
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        // Never truncate: half a word is worse than no pill.
        flex: "0 0 auto",
        ...style,
      }}
    >
      <span className="flex items-center" style={{ gap: stacked ? 5 : 8 }}>
        {/* A 6px dot inside a soft ring of its own colour, rather than an 8px
            disc — the ring is what makes it read as a status light. */}
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "999px",
            background: isMarketOpen ? "#229564" : "var(--cat-accent, #5B1DEE)",
            boxShadow: isMarketOpen
              ? "0 0 0 3px rgba(34, 149, 100, .18)"
              : "0 0 0 3px var(--cat-accent-soft, rgba(91, 29, 238, .18))",
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontFamily: "General Sans",
            fontSize: stacked ? "11px" : "12px",
            fontWeight: 500,
            lineHeight: stacked ? 1.2 : "100%",
            // `--t3-ink` is a cockpit token and is undefined everywhere else, so
            // this fell through to solid black — invisible on a dark chart. The
            // generic ink token is defined in both themes (and reset to its light
            // value inside the Pokédex, which is exactly what the pill wants).
            color: stacked ? "var(--ink-3)" : "var(--ink, #1A1A1A)",
            whiteSpace: "nowrap",
          }}
        >
          {isMarketOpen ? (scheduledKind ? "Market Open" : "Open") : (scheduledKind ? "Market Opens In" : "Next Price Update")}
        </span>
      </span>
      <span
        style={{
          fontFamily: "General Sans",
          fontSize: stacked ? "15px" : "12px",
          fontWeight: stacked ? 700 : 600,
          lineHeight: 1.2,
          // Tabular figures: a clock that reflows its own width every second
          // drags the row with it.
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-.02em",
          color: isMarketOpen ? "#229564" : "var(--cat-accent, #5B1DEE)",
          whiteSpace: "nowrap",
        }}
      >
        {/* The seconds place changes every tick, so it rolls rather than
            swaps. No stagger and a sub-second roll: at 1Hz a staggered clock
            would still be settling when the next second lands, and only the
            digits that actually changed move — the hours sit still. */}
        <NumberTicker text={clock} duration={0.34} stagger={0} startOnView={false} />
      </span>
    </div>
  );
};

export default PriceUpdateBadge;
